import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { pdf } from "@react-pdf/renderer";
import { Button, QRCode } from "antd";
import { isEmpty } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";

import DatePicker from "../../../Common/DatePicker/DatePicker";
import PdfPreviewModal from "../../../Common/UIComponents/PdfPreviewModal";
import LabelsPDF from "./LabelsPDF";
import ZebraPrintModal from "./ZebraPrintModal";

import { hasPermission, permissions } from "../../../../utils/Permissions";
import { getFormattedDaysAgoString } from "../../../Common/DatePicker/utils";

import { getEnvSchedule } from "../../../../actions/envCalendar";

import { ReactComponent as CalendarIcon } from "../../../../assets/images/environment/CalendarEnv.svg";
import { ReactComponent as PrinterIcon } from "../../../../assets/images/environment/PrinterIcon.svg";

import "./PrintLabelModal.css";

const waitForQrCanvases = async (items) => {
  const started = Date.now();
  while (Date.now() - started < 2000) {
    const ready = items.every((item) => {
      const node = document.getElementById(`GeneratedQR__${item.submission_id}`);
      const canvas = node?.querySelector("canvas");
      return canvas && canvas.width > 0;
    });
    if (ready) return true;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return false;
};

export default function PrintLabelModal({
  eventData,
  onClose,
  defaultDateRange,
  allowClear = true,
  submissionCategory = "",
  specFilterValues = [],
}) {
  const initialDateRange = getFormattedDaysAgoString(
    defaultDateRange.dateFromObj,
    defaultDateRange.dateToObj,
  );
  const [generatingPDF, setGeneratingPDF] = useState(!eventData);
  const [scheduleReady, setScheduleReady] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [scheduleData, setScheduleData] = useState([]);
  const [markedDates, setMarkedDates] = useState([]);
  const [isPrintLabelOpen, setIsPrintLabelOpen] = useState(false);
  const [dateSelected, setDateSelected] = useState(initialDateRange);
  const [prevDateSelected, setPrevDateSelected] = useState(initialDateRange);

  const controller = useRef();

  /**
   * Calls envschedule/ api
   * @param {Date} start start date
   * @param {Date} end end date
   */
  const apiGetEnvSchedule = async (start, end) => {
    const abortController = new AbortController();
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = abortController;
    const specFilters = specFilterValues.join(",");
    const result = await getEnvSchedule(
      start,
      end,
      specFilters,
      submissionCategory,
      controller.current.signal,
    );

    if (controller.current.signal.aborted) {
      setScheduleData([]);
      setMarkedDates([]);
    }

    if (result && result.success) {
      const data = result.result;
      const sampleWiseData = [];
      (data.scheduled_submission ?? []).forEach((submission) => {
        const sample_data = submission.samples_list.map((sample) => ({
          event_id: submission.event_id,
          submission_name: sample.sample_id,
          submission_id: sample.submission_id,
          event_date: moment(submission.event_date).format("MMM D, YYYY"),
        }));
        sampleWiseData.push(...sample_data);
      });
      setScheduleData(sampleWiseData);
      setMarkedDates(data.scheduled_submission_dates);
    }

    controller.current = null;
  };

  useEffect(() => {
    if (eventData) {
      const sample_data = eventData.samples_list.map((sample) => ({
        event_id: eventData.event_id,
        submission_name: sample.sample_id,
        submission_id: sample.submission_id,
        event_date: moment(eventData.event_date).format("MMM D, YYYY"),
      }));
      setScheduleData(sample_data);
      setScheduleReady(true);
    } else {
      setGeneratingPDF(true);
      const { dateFromString, dateToString } = defaultDateRange;
      apiGetEnvSchedule(dateFromString, dateToString).finally(() => {
        setGeneratingPDF(false);
        setScheduleReady(true);
      });
    }
  }, [eventData]); // eslint-disable-line

  useEffect(() => {
    if (!scheduleReady || scheduleData.length === 0) {
      setPdfBlob(null);
      setPdfLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPdfLoading(true);
    setPdfFailed(false);
    setPdfBlob(null);

    const generatePdf = async () => {
      const fileName = `Print_Label_${moment().unix()}.pdf`;
      try {
        const ready = await waitForQrCanvases(scheduleData);
        if (cancelled) return;
        if (!ready) {
          toast.error("Failed to generate pdf");
          setPdfFailed(true);
          return;
        }

        const blob = await pdf(LabelsPDF({
          getFileName: () => fileName,
          scheduleData,
        })).toBlob();
        if (cancelled) return;

        if (!blob || blob.size === 0) {
          toast.error("Failed to generate pdf");
          setPdfFailed(true);
          setPdfBlob(null);
          return;
        }

        setPdfFileName(fileName);
        setPdfBlob(blob);
      } catch (error) {
        if (cancelled) return;
        toast.error("Failed to generate pdf");
        setPdfFailed(true);
        setPdfBlob(null);
      } finally {
        if (!cancelled) {
          setPdfLoading(false);
        }
      }
    };

    generatePdf();

    return () => {
      cancelled = true;
    };
  }, [scheduleData, scheduleReady]);

  const handlePrint = () => {
    setIsPrintLabelOpen(true);
  };

  /**
   * handle date picker change event
   * @param {Date} startDate Start date
   * @param {Date} endDate End date
   */
  const handleDatePickerChange = async (startDate, endDate) => {
    if (eventData) return; // If print modal is opened from the event card i.e. having only one event data

    const start = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
    const end = endDate ? moment(endDate).format("YYYY-MM-DD") : "";
    setGeneratingPDF(true);
    setPdfBlob(null);
    setPdfFailed(false);
    await apiGetEnvSchedule(start, end);
    setGeneratingPDF(false);
  };

  const showDatePicker = !eventData;
  const canPrint = hasPermission(permissions.environment.calendarPrinter);
  const previewLoading = !scheduleReady || generatingPDF || pdfLoading || (scheduleData.length > 0 && !pdfBlob && !pdfFailed);
  const previewFiles = useMemo(() => {
    if (!pdfBlob) return [];
    return [{
      key: "scheduling-plan",
      fileName: pdfFileName || "Print_Label.pdf",
      src: pdfBlob,
    }];
  }, [pdfBlob, pdfFileName]);

  return (
    <>
      <div className="PrintLabelModal__QrSource" aria-hidden>
        {scheduleData.map((data, i) => {
          const key = `${data.submission_id}-${data.event_date}-${i}`;
          return (
            <div id={`GeneratedQR__${data.submission_id}`} key={key}>
              <QRCode
                status={data.submission_id === "" ? "loading" : "active"}
                value={data.submission_id}
                type="canvas"
                size={180}
                bordered={false}
              />
            </div>
          );
        })}
      </div>
      <PdfPreviewModal
        open
        title="Print Scheduling Plan"
        onClose={onClose}
        files={previewFiles}
        loading={previewLoading}
        showDownload={false}
        showPrint={false}
        errorMessage="Preview not available."
        width="40vw"
        headerExtra={(showDatePicker || canPrint) ? (
          <div className={`PrintLabelModal__Toolbar${showDatePicker ? "" : " PrintLabelModal__Toolbar--end"}`}>
            {showDatePicker && (
              <DatePicker
                handleSearchDate={handleDatePickerChange}
                getReportDates={() => markedDates ?? []}
                value={dateSelected}
                onChange={setDateSelected}
                prevValue={prevDateSelected}
                setPrevValue={setPrevDateSelected}
                searchOnClear
                popoverPlacement="bottomLeft"
                allowClear={allowClear}
                datePickerDisplayProps={{
                  displayTextPosition: "right",
                  displayClassName: "ReportModal__DatePicker",
                  customCalendarIcon: <CalendarIcon width={16} height={16} />,
                }}
                maxDate={moment().add(1, "year").endOf("month").format("MM/DD/YYYY")}
              />
            )}
            {canPrint && (
              <Button
                type="primary"
                ghost
                onClick={handlePrint}
                icon={<PrinterIcon />}
              >
                Print
              </Button>
            )}
          </div>
        ) : null}
      />
      {isPrintLabelOpen && (
        <ZebraPrintModal
          disablePrintButton={generatingPDF || pdfLoading || isEmpty(scheduleData)}
          scheduleData={scheduleData}
          totalItems={scheduleData?.length}
          onClose={() => setIsPrintLabelOpen(false)}
        />
      )}
    </>
  );
}
